import React, { useEffect, useState } from "react";
import { mockProducts } from "../../../Components/Data/MockData";
import DiscountModal from "../../../Components/Modals/DiscounTier/DiscountTier";
import ProductModal from "../../../Components/Modals/Products/AddProducts";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDiscountTier } from "./useHook";
import { useProducts } from "../ProductList/useHook";
import FeaturedProductModal from "../../../Components/Modals/FeaturedProducts/FeaturedProduct";
import ReactPlayer from "react-player";
import Loader, { ImageWithFallback } from "../../../Utils/loader";

const ProductDetails = () => {
  const {
    showOnHomeScreen,
    getProductData,
    addStock,
    showProductOnHomeScreen,
    getCategories,
    updateVariantStock,
    updateVariantStockDetails,
    updateMultipleVariantStock,
  } = useDiscountTier();
  const { getBrands } = useProducts();
  const [productData, setProductData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const { id } = location?.state || {};
  const [formData, setFormData] = useState({ ...mockProducts });
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFeaturedModalOpen, setIsFeaturedModalOpen] = useState(false);

  const [stockQuantity, setStockQuantity] = useState(0);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0); // Track selected variant
  const [selectedColorIndex, setSelectedColorIndex] = useState(0); // Track selected color within variant
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("shortDescription");
  const [isHomeScreenLoading, setIsHomeScreenLoading] = useState(false);
  const [isVariantStockLoading, setIsVariantStockLoading] = useState(null); // Track which color ID is loading
  const [isVariantStockModalOpen, setIsVariantStockModalOpen] = useState(false);
  const [selectedColorForEdit, setSelectedColorForEdit] = useState(null); // Track which color is being edited
  const [variantStockFormData, setVariantStockFormData] = useState({
    totalProducts: "",
    remainingProduct: "",
    moq: "",
    isInStock: true,
  });
  const [expandedVariants, setExpandedVariants] = useState(new Set()); // Track which variants are expanded

  useEffect(() => {
    if (id) {
      const fetchData = async () => {
        setIsLoading(true);
        try {
          await Promise.all([
            getProductData(id, setProductData),
            getCategories(setCategories),
            getBrands(setBrands),
          ]);
        } catch (error) {
          console.error("Error fetching product data:", error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchData();
    }
  }, [id]);

  useEffect(() => {
    if (productData) {
      // Transform new API response structure - keep variants grouped
      let transformedData = { ...productData };

      // If product has variants structure (new API format)
      if (
        productData.variants &&
        Array.isArray(productData.variants) &&
        productData.variants.length > 0
      ) {
        // Process variants and their colors, keeping structure intact
        const processedVariants = productData.variants.map((variant) => {
          const processedColors = (variant.colors || []).map((color) => {
            // Extract images from new structure: color.images is array of objects with imageUrl
            let imageUrls = [];
            if (Array.isArray(color.images)) {
              imageUrls = color.images.map((img) => {
                // Handle both new structure (object with imageUrl) and old structure (string)
                return typeof img === "object" && img.imageUrl
                  ? img.imageUrl
                  : img;
              });
            } else if (Array.isArray(color.image)) {
              // Fallback to old structure
              imageUrls = color.image;
            }

            // Add variant image as first image if available (for first color of variant)
            const isFirstColor = variant.colors.indexOf(color) === 0;
            if (isFirstColor && variant.variant_image) {
              imageUrls = [variant.variant_image, ...imageUrls];
            }

            return {
              id: color.id || null,
              color: color.color || "",
              image: imageUrls,
              // Stock data comes from color level (not variant level)
              moq:
                color.moq !== undefined && color.moq !== null
                  ? color.moq
                  : null,
              totalProducts:
                color.totalProducts !== undefined ? color.totalProducts : 0,
              remainingProduct:
                color.remainingProduct !== undefined
                  ? color.remainingProduct
                  : 0,
              isInStock:
                color.inStock !== undefined ? color.inStock !== false : true, // Map color.inStock to isInStock for UI
            };
          });

          // Calculate variant stock status based on colors (if any color is in stock, variant is in stock)
          const hasInStockColor = processedColors.some(
            (color) => color.isInStock !== false,
          );

          return {
            id: variant.id || null,
            name: variant.name || "",
            price: variant.price || "",
            weight: variant.weight || "",
            dimension: variant.dimension || "",
            unit: variant.unit || "",
            variant_image: variant.variant_image || null,
            isInStock: hasInStockColor, // Calculate from colors, not from variant level
            colors: processedColors,
          };
        });

        transformedData.variants = processedVariants;

        // Also keep a flattened version for backward compatibility if needed
        const colorsArray = [];
        processedVariants.forEach((variant) => {
          variant.colors.forEach((color) => {
            colorsArray.push({
              ...color,
              name: variant.name,
              price: variant.price,
              weight: variant.weight,
              dimension: variant.dimension,
              unit: variant.unit,
              variantId: variant.id,
            });
          });
        });
        transformedData.colors = colorsArray;
      } else if (productData.colors && Array.isArray(productData.colors)) {
        // Old structure - ensure images are in correct format
        transformedData.colors = productData.colors.map((color) => {
          // Handle new image structure if present
          if (Array.isArray(color.images)) {
            return {
              ...color,
              image: color.images.map((img) => {
                return typeof img === "object" && img.imageUrl
                  ? img.imageUrl
                  : img;
              }),
            };
          }
          return color;
        });
      }

      setFormData(transformedData);

      // Initialize selected variant and color
      if (transformedData.variants && transformedData.variants.length > 0) {
        // Auto-select first variant
        setSelectedVariantIndex(0);
        // Auto-select first in-stock color in first variant if available
        const firstVariant = transformedData.variants[0];
        if (firstVariant.colors && firstVariant.colors.length > 0) {
          const firstInStockIndex = firstVariant.colors.findIndex(
            (color) => color.isInStock !== false,
          );
          const initialColorIndex =
            firstInStockIndex !== -1 ? firstInStockIndex : 0;
          setSelectedColorIndex(initialColorIndex);
          setSelectedImageIndex(0);
          // Expand first variant by default
          setExpandedVariants(new Set([0]));
        }
      } else if (transformedData.colors && transformedData.colors.length > 0) {
        // Old structure - auto-select first in-stock color if available
        const firstInStockIndex = transformedData.colors.findIndex(
          (color) => color.isInStock !== false,
        );
        const initialIndex = firstInStockIndex !== -1 ? firstInStockIndex : 0;
        setSelectedColorIndex(initialIndex);
        setSelectedImageIndex(0);
      }
    }
  }, [productData]);

  const handleStockUpdate = () => {
    setStockQuantity(productData?.totalProducts || 0);
    setIsStockModalOpen(true);
  };

  const handleHomeScreenItem = async () => {
    if (!productData?.id) return;

    setIsHomeScreenLoading(true);
    const newStatus = !productData.homeScreen;

    try {
      await showProductOnHomeScreen(productData.id, newStatus ? 1 : 0);
      await getProductData(productData.id, setProductData);
    } catch (error) {
      console.error("Failed to update home screen status:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update home screen status. Please try again.",
        icon: "error",
        timer: 2000,
        showConfirmButton: false,
      });
    } finally {
      setIsHomeScreenLoading(false);
    }
  };

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleStockChange = (e) => {
    let value = e.target.value;
    if (value.length > 1 && value.startsWith("0")) {
      value = value.replace(/^0+/, "");
    }
    setStockQuantity(value);
  };

  const handleStockSubmit = () => {
    const numericValue = parseInt(stockQuantity, 10);
    if (isNaN(numericValue) || numericValue <= 0) {
      Swal.fire(
        "Invalid Quantity",
        "Please enter a valid quantity greater than 0",
        "error",
      );
      return;
    }
    addStock(id, numericValue).then(() => {
      setIsStockModalOpen(false);
      setStockQuantity(0);
      getProductData(id, setProductData);
    });
  };

  const handleAddDiscountDetail = () => {
    setIsDiscountModalOpen(true);
  };
  const closeDiscountModal = () => {
    setIsDiscountModalOpen(false);
  };

  const handleAddFeaturedProduct = () => {
    setIsFeaturedModalOpen(true);
  };
  const closeFeaturedModal = () => {
    setIsFeaturedModalOpen(false);
  };

  const handleToggleVariantStock = async (colorObj, currentStatus) => {
    // Stock is now managed at the variant color level (colorId)
    const colorId = colorObj.id;
    if (!colorId) return;

    setIsVariantStockLoading(colorId); // Use color ID for loading state tracking
    try {
      const newStatus = !currentStatus;
      await updateVariantStock(colorId, newStatus);
      // Refresh product data to get updated stock status
      if (id) {
        await getProductData(id, setProductData);
      }
    } catch (error) {
      console.error("Error updating variant stock:", error);
    } finally {
      setIsVariantStockLoading(null); // Reset to null when done
    }
  };

  const handleEditVariantStock = (colorObj) => {
    setSelectedColorForEdit(colorObj);
    setVariantStockFormData({
      totalProducts:
        colorObj.totalProducts !== undefined && colorObj.totalProducts !== null
          ? colorObj.totalProducts
          : "",
      remainingProduct:
        colorObj.remainingProduct !== undefined &&
        colorObj.remainingProduct !== null
          ? colorObj.remainingProduct
          : "",
      moq:
        colorObj.moq !== undefined && colorObj.moq !== null ? colorObj.moq : "",
      isInStock: colorObj.isInStock !== false,
    });
    setIsVariantStockModalOpen(true);
  };

  const toggleVariantExpansion = (variantIndex) => {
    setExpandedVariants((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(variantIndex)) {
        newSet.delete(variantIndex);
      } else {
        newSet.add(variantIndex);
        // Auto-select this variant when expanding
        setSelectedVariantIndex(variantIndex);
        // Auto-select first color in this variant
        if (
          formData.variants &&
          formData.variants[variantIndex]?.colors?.length > 0
        ) {
          const firstInStockIndex = formData.variants[
            variantIndex
          ].colors.findIndex((color) => color.isInStock !== false);
          const initialColorIndex =
            firstInStockIndex !== -1 ? firstInStockIndex : 0;
          setSelectedColorIndex(initialColorIndex);
          setSelectedImageIndex(0);
        }
      }
      return newSet;
    });
  };

  const handleVariantStockFormChange = (field, value) => {
    setVariantStockFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleVariantStockSubmit = async () => {
    if (!selectedColorForEdit?.id) return;

    const colorId = selectedColorForEdit.id;

    // Validation
    if (
      variantStockFormData.totalProducts !== "" &&
      parseInt(variantStockFormData.totalProducts) < 0
    ) {
      Swal.fire(
        "Error",
        "Total products must be a non-negative integer",
        "error",
      );
      return;
    }
    if (
      variantStockFormData.remainingProduct !== "" &&
      parseInt(variantStockFormData.remainingProduct) < 0
    ) {
      Swal.fire(
        "Error",
        "Remaining products must be a non-negative integer",
        "error",
      );
      return;
    }
    if (
      variantStockFormData.moq !== "" &&
      variantStockFormData.moq !== "0" &&
      variantStockFormData.moq !== null
    ) {
      const moqValue = parseInt(variantStockFormData.moq);
      if (isNaN(moqValue) || moqValue < 1 || moqValue > 10000) {
        Swal.fire(
          "Error",
          "MOQ must be between 1 and 10000, or 0/null to clear",
          "error",
        );
        return;
      }
    }
    if (
      variantStockFormData.totalProducts !== "" &&
      variantStockFormData.remainingProduct !== ""
    ) {
      if (
        parseInt(variantStockFormData.remainingProduct) >
        parseInt(variantStockFormData.totalProducts)
      ) {
        Swal.fire(
          "Error",
          "Remaining products cannot exceed total products",
          "error",
        );
        return;
      }
    }
    if (
      variantStockFormData.isInStock &&
      variantStockFormData.remainingProduct === "0"
    ) {
      Swal.fire(
        "Error",
        "Cannot mark variant back to stock when remainingProduct is 0",
        "error",
      );
      return;
    }

    // Build payload according to new API: colorId is required, other fields are optional
    const payload = {
      colorId: colorId,
    };

    // Only include fields that are provided (not empty strings)
    if (variantStockFormData.totalProducts !== "") {
      payload.totalProducts = parseInt(variantStockFormData.totalProducts);
    }
    if (variantStockFormData.remainingProduct !== "") {
      payload.remainingProduct = parseInt(
        variantStockFormData.remainingProduct,
      );
    }
    if (variantStockFormData.moq !== "" && variantStockFormData.moq !== "0") {
      payload.moq = parseInt(variantStockFormData.moq);
    } else if (
      variantStockFormData.moq === "0" ||
      variantStockFormData.moq === null
    ) {
      // Allow clearing MOQ by setting to null or 0
      payload.moq = null;
    }
    // Always include inStock (API expects inStock, not isInStock)
    payload.inStock = variantStockFormData.isInStock;

    // Validate that at least one optional field is provided
    const hasOptionalField =
      payload.totalProducts !== undefined ||
      payload.remainingProduct !== undefined ||
      payload.moq !== undefined ||
      payload.inStock !== undefined;

    if (!hasOptionalField) {
      Swal.fire(
        "Error",
        "At least one field (inStock, totalProducts, remainingProduct, moq) must be provided",
        "error",
      );
      return;
    }

    setIsVariantStockLoading(colorId); // Set the specific color ID being updated
    try {
      await updateVariantStockDetails(payload);
      setIsVariantStockModalOpen(false);
      setSelectedColorForEdit(null);
      // Refresh product data
      if (id) {
        await getProductData(id, setProductData);
      }
    } catch (error) {
      console.error("Error updating variant stock details:", error);
    } finally {
      setIsVariantStockLoading(null); // Reset to null when done
    }
  };

  if (isLoading) {
    return <Loader message="Loading product details..." />;
  }

  if (!productData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-xl font-semibold">Product not found</p>
      </div>
    );
  }

  const handleProductModalSuccess = () => {
    if (id) {
      getProductData(id, setProductData);
    }
  };

  return (
    <>
      {/* Top Header */}
      <div className="bg-gray-100 p-4 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold mb-1">Product Details</h2>
          {/* <p className="mt-2">
            Available Stock: {productData?.totalProducts || "Not Added"}
          </p> */}
        </div>
        <div className="flex space-x-4">
          {/*  <button
            className={`py-2 px-4 rounded flex items-center space-x-2 ${isHomeScreenLoading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-500 hover:bg-blue-600"
              } text-white`}
            onClick={handleHomeScreenItem}
            disabled={isHomeScreenLoading}
          >
            {isHomeScreenLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Updating...</span>
              </>
            ) : (
              <span>
                {productData?.homeScreen
                  ? "Hide from home screen"
                  : "Home page Priority"}
              </span>
            )}
          </button> */}
          <button
            onClick={handleEditClick}
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
          >
            Edit
          </button>
          {/* <button
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
            onClick={handleStockUpdate}
          >
            Update Stock Availability
          </button> */}
          <button
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
            onClick={handleAddDiscountDetail}
          >
            Add Discount Detail
          </button>
          {/* NEW: Add Related Product Button */}
          <button
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
            onClick={handleAddFeaturedProduct}
          >
            Add Related/Similar Product
          </button>
        </div>
      </div>

      {/* Main Product Info */}
      <div className="my-6 p-6 bg-white shadow-lg rounded-lg">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Product Information */}
          <div className="md:w-full">
            <div className="flex">
              <h2 className="text-3xl mb-4">Product Name:</h2>
              <h2 className="text-3xl font-bold mb-4">
                {formData.name || "Unnamed Product"}
              </h2>
            </div>
            <div className="flex gap-8">
              <p className="mb-2">
                <span className="font-semibold">Status: </span>
                <span
                  className={`font-semibold ${
                    formData.status ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {formData.status ? "Active" : "Inactive"}
                </span>
              </p>
              <p className="mb-2">
                <span className="font-semibold">Category: </span>
                <span
                  className={`font-semibold ${
                    formData.category ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {formData.category?.name || "N/A"}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Tabbed Descriptions */}
        <div className="mt-6 border-t pt-4">
          <div className="flex border-b border-gray-300">
            <button
              className={`py-2 px-4 font-medium ${
                activeTab === "shortDescription"
                  ? "border-b-2 border-blue-500 text-blue-500"
                  : "text-gray-600"
              }`}
              onClick={() => setActiveTab("shortDescription")}
            >
              Short Description
            </button>
            <button
              className={`py-2 px-4 font-medium ${
                activeTab === "description"
                  ? "border-b-2 border-blue-500 text-blue-500"
                  : "text-gray-600"
              }`}
              onClick={() => setActiveTab("description")}
            >
              Description
            </button>
            <button
              className={`py-2 px-4 font-medium ${
                activeTab === "addiotionalInformation"
                  ? "border-b-2 border-blue-500 text-blue-500"
                  : "text-gray-600"
              }`}
              onClick={() => setActiveTab("addiotionalInformation")}
            >
              Additional Info
            </button>
            <button
              className={`py-2 px-4 font-medium ${
                activeTab === "productVideo"
                  ? "border-b-2 border-blue-500 text-blue-500"
                  : "text-gray-600"
              }`}
              onClick={() => setActiveTab("productVideo")}
            >
              Product Video
            </button>
          </div>
          <div className="p-4">
            {activeTab === "shortDescription" && (
              <p className="break-words whitespace-pre-wrap">
                {formData.shortDescription || "No short description available"}
              </p>
            )}
            {activeTab === "description" && (
              <div className="text-sm text-gray-600 mb-4 break-words whitespace-pre-wrap">
                <strong>Description:</strong>
                <div
                  className="mt-1 break-words whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{
                    __html: formData.description || "No description available",
                  }}
                />
              </div>
            )}
            {activeTab === "addiotionalInformation" && (
              <p className="break-words whitespace-pre-wrap">
                {formData.additionalInformation ||
                  "No additional information available"}
              </p>
            )}
            {activeTab === "productVideo" && (
              <div className="flex justify-center my-6">
                <div className="">
                  {productData.videoUrl ? (
                    <ReactPlayer
                      url={productData.videoUrl}
                      controls
                      width="100%"
                      height="auto"
                    />
                  ) : (
                    <p className="p-4 text-center text-gray-500">
                      No video available.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Product Variants */}
      {formData?.variants && formData.variants.length > 0 && (
        <div className="mt-6 p-6 bg-white shadow-md rounded-lg">
          <h2 className="text-2xl font-bold mb-4">Product Variants</h2>

          {/* Variants List */}
          <div className="space-y-4">
            {formData.variants.map((variant, variantIndex) => {
              const isExpanded = expandedVariants.has(variantIndex);
              const isSelected = selectedVariantIndex === variantIndex;
              const selectedColor = isSelected
                ? variant.colors?.[selectedColorIndex]
                : null;

              return (
                <div
                  key={variant.id || variantIndex}
                  className={`border-2 rounded-lg transition-all ${
                    isSelected ? "border-blue-500 shadow-md" : "border-gray-200"
                  }`}
                >
                  {/* Variant Header - Clickable */}
                  <div
                    className="p-4 bg-gray-50 rounded-t-lg cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => toggleVariantExpansion(variantIndex)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <h3 className="text-xl font-semibold text-gray-800">
                          {variant.name || `Variant ${variantIndex + 1}`}
                        </h3>
                        <span className="text-sm text-gray-600">
                          {formData.currency} {variant.price || "N/A"}
                        </span>
                        {variant.weight && (
                          <span className="text-sm text-gray-600">
                            {variant.weight} {variant.unit || ""}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span
                          className={`px-3 py-1 rounded text-xs font-semibold ${
                            variant.isInStock
                              ? "bg-green-200 text-green-800"
                              : "bg-red-200 text-red-800"
                          }`}
                        >
                          {variant.isInStock ? "In Stock" : "Out of Stock"}
                        </span>
                        <svg
                          className={`w-5 h-5 text-gray-600 transition-transform ${
                            isExpanded ? "transform rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Variant Content */}
                  {isExpanded && (
                    <div className="p-4">
                      {/* Stock Management Section for Variant Colors */}
                      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <h4 className="text-lg font-semibold mb-3">
                          Manage Stock by Color
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {variant.colors.map((colorObj, colorIdx) => {
                            const isInStock = colorObj.isInStock !== false;
                            return (
                              <div
                                key={colorObj.id || colorIdx}
                                className={`p-3 rounded-lg border-2 ${
                                  isInStock
                                    ? "border-green-300 bg-green-50"
                                    : "border-red-300 bg-red-50 opacity-75"
                                }`}
                              >
                                <div className="mb-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-3">
                                      <div
                                        className="w-10 h-10 rounded-full border-2 border-gray-300 flex-shrink-0"
                                        style={{
                                          backgroundColor:
                                            colorObj.color || "transparent",
                                        }}
                                        title={colorObj.color || "No Color"}
                                      ></div>
                                      <div className="flex flex-col">
                                        <span className="text-sm font-semibold text-gray-800">
                                          {colorObj.color || "No Color"}
                                        </span>
                                        <span
                                          className={`text-xs font-semibold ${
                                            isInStock
                                              ? "text-green-700"
                                              : "text-red-700"
                                          }`}
                                        >
                                          {isInStock
                                            ? "In Stock"
                                            : "Out of Stock"}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-2 space-y-2">
                                  <button
                                    className={`w-full py-1.5 px-3 rounded text-xs font-medium transition-colors ${
                                      isInStock
                                        ? "bg-red-500 hover:bg-red-600 text-white"
                                        : "bg-green-500 hover:bg-green-600 text-white"
                                    } ${
                                      isVariantStockLoading === colorObj.id
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      handleToggleVariantStock(
                                        colorObj,
                                        isInStock,
                                      )
                                    }
                                    disabled={
                                      isVariantStockLoading === colorObj.id
                                    }
                                  >
                                    {isVariantStockLoading === colorObj.id
                                      ? "Updating..."
                                      : isInStock
                                        ? "Mark Out of Stock"
                                        : "Mark In Stock"}
                                  </button>
                                  <button
                                    className="w-full py-1.5 px-3 rounded text-xs font-medium transition-colors bg-blue-500 hover:bg-blue-600 text-white"
                                    onClick={() =>
                                      handleEditVariantStock(colorObj)
                                    }
                                  >
                                    Edit Stock Details
                                  </button>
                                </div>
                                <div className="mt-2 text-xs text-gray-600 space-y-1">
                                  <p>
                                    Total:{" "}
                                    {colorObj.totalProducts !== undefined
                                      ? colorObj.totalProducts
                                      : 0}
                                  </p>
                                  <p>
                                    Remaining:{" "}
                                    {colorObj.remainingProduct !== undefined
                                      ? colorObj.remainingProduct
                                      : 0}
                                  </p>
                                  <p>
                                    MOQ:{" "}
                                    {colorObj.moq !== undefined &&
                                    colorObj.moq !== null
                                      ? colorObj.moq
                                      : "Not set"}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Color Selection and Images */}
                      {variant.colors && variant.colors.length > 0 && (
                        <div className="mt-4">
                          {/* Color Swatches */}
                          <div className="flex flex-wrap gap-3 mb-4">
                            {variant.colors.map((colorObj, colorIdx) => {
                              const isInStock = colorObj.isInStock !== false;
                              const isColorSelected =
                                isSelected && selectedColorIndex === colorIdx;
                              return (
                                <div
                                  key={colorObj.id || colorIdx}
                                  className={`relative w-12 h-12 rounded-full cursor-pointer border-2 transition-all ${
                                    isColorSelected
                                      ? "border-blue-500 ring-2 ring-blue-300"
                                      : "border-gray-300"
                                  } ${!isInStock ? "opacity-60" : ""}`}
                                  style={{
                                    backgroundColor:
                                      colorObj.color || "transparent",
                                  }}
                                  onClick={() => {
                                    // Allow selecting any color to view images, regardless of stock status
                                    setSelectedVariantIndex(variantIndex);
                                    setSelectedColorIndex(colorIdx);
                                    setSelectedImageIndex(0);
                                  }}
                                  title={`${colorObj.color || "No Color"} - ${
                                    isInStock ? "In Stock" : "Out of Stock"
                                  }`}
                                >
                                  {!isInStock && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <span className="text-xs font-bold text-red-600">
                                        ×
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Main Image and Thumbnails for Selected Color */}
                          {selectedColor &&
                            selectedColor.image &&
                            selectedColor.image.length > 0 && (
                              <div>
                                <div className="relative">
                                  <ImageWithFallback
                                    src={
                                      selectedColor.image[
                                        Math.min(
                                          selectedImageIndex,
                                          selectedColor.image.length - 1,
                                        )
                                      ]
                                    }
                                    alt={`Main view for ${selectedColor.color || "Color"}`}
                                    className={`w-[450px] h-[450px] object-cover rounded ${
                                      selectedColor.isInStock === false
                                        ? "opacity-60"
                                        : ""
                                    }`}
                                  />
                                  {selectedColor.isInStock === false && (
                                    <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-md font-semibold text-sm">
                                      Out of Stock
                                    </div>
                                  )}
                                </div>
                                <div className="mt-2 flex space-x-2">
                                  {selectedColor.image.map((imgUrl, idx) => (
                                    <div
                                      key={idx}
                                      className={`relative rounded cursor-pointer transition-all hover:opacity-80 ${
                                        selectedImageIndex === idx
                                          ? "border-2 border-blue-500 ring-2 ring-blue-300"
                                          : "border-2 border-gray-300 hover:border-gray-400"
                                      }`}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedImageIndex(idx);
                                      }}
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === "Enter" ||
                                          e.key === " "
                                        ) {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setSelectedImageIndex(idx);
                                        }
                                      }}
                                      role="button"
                                      tabIndex={0}
                                      aria-label={`Select image ${idx + 1}`}
                                    >
                                      <ImageWithFallback
                                        src={imgUrl}
                                        alt={`Thumbnail ${idx + 1}`}
                                        className="w-20 h-20 object-cover rounded"
                                      />
                                    </div>
                                  ))}
                                </div>
                                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                  <h4 className="text-lg font-semibold mb-3">
                                    Variant Details
                                  </h4>
                                  <div className="grid grid-cols-2 gap-3">
                                    <p className="text-sm">
                                      <span className="font-semibold">
                                        Variant Name:
                                      </span>{" "}
                                      <span className="text-gray-700">
                                        {variant.name || "N/A"}
                                      </span>
                                    </p>
                                    <p className="text-sm">
                                      <span className="font-semibold">
                                        Color:
                                      </span>{" "}
                                      <span className="text-gray-700">
                                        {selectedColor.color || "N/A"}
                                      </span>
                                    </p>
                                    <p className="text-sm">
                                      <span className="font-semibold">
                                        Price:
                                      </span>{" "}
                                      <span className="text-gray-700">
                                        {formData.currency}{" "}
                                        {variant.price || "N/A"}
                                      </span>
                                    </p>
                                    {variant.weight && (
                                      <p className="text-sm">
                                        <span className="font-semibold">
                                          Weight:
                                        </span>{" "}
                                        <span className="text-gray-700">
                                          {variant.weight}
                                          {variant.unit
                                            ? ` ${variant.unit}`
                                            : ""}
                                        </span>
                                      </p>
                                    )}
                                    {variant.dimension && (
                                      <p className="text-sm">
                                        <span className="font-semibold">
                                          Dimension:
                                        </span>{" "}
                                        <span className="text-gray-700">
                                          {variant.dimension || "N/A"}
                                        </span>
                                      </p>
                                    )}
                                    {variant.unit && (
                                      <p className="text-sm">
                                        <span className="font-semibold">
                                          Unit:
                                        </span>{" "}
                                        <span className="text-gray-700">
                                          {variant.unit || "N/A"}
                                        </span>
                                      </p>
                                    )}
                                    <p className="text-sm">
                                      <span className="font-semibold">
                                        MOQ:
                                      </span>{" "}
                                      <span className="text-gray-700">
                                        {selectedColor.moq !== undefined &&
                                        selectedColor.moq !== null
                                          ? selectedColor.moq
                                          : "Not set"}
                                      </span>
                                    </p>
                                    <p className="text-sm">
                                      <span className="font-semibold">
                                        Total Products:
                                      </span>{" "}
                                      <span className="text-gray-700">
                                        {selectedColor.totalProducts !==
                                        undefined
                                          ? selectedColor.totalProducts
                                          : "N/A"}
                                      </span>
                                    </p>
                                    <p className="text-sm">
                                      <span className="font-semibold">
                                        Remaining Products:
                                      </span>{" "}
                                      <span className="text-gray-700">
                                        {selectedColor.remainingProduct !==
                                        undefined
                                          ? selectedColor.remainingProduct
                                          : "N/A"}
                                      </span>
                                    </p>
                                    <p className="text-sm">
                                      <span className="font-semibold">
                                        Stock Status:
                                      </span>{" "}
                                      <span
                                        className={`font-semibold ${
                                          selectedColor.isInStock !== false
                                            ? "text-green-600"
                                            : "text-red-600"
                                        }`}
                                      >
                                        {selectedColor.isInStock !== false
                                          ? "In Stock"
                                          : "Out of Stock"}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Fallback for old structure (no variants) */}
      {(!formData?.variants || formData.variants.length === 0) &&
        formData?.colors &&
        formData.colors.length > 0 && (
          <div className="mt-6 p-6 bg-white shadow-md rounded-lg">
            <h2 className="text-2xl font-bold mb-4">
              Product Images by Varient
            </h2>
            {/* Old structure display - keep existing code if needed */}
          </div>
        )}

      {/* Stock Modal */}
      {isStockModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <h2 className="text-lg font-semibold mb-4">
              Update Stock Quantity
            </h2>
            <input
              type="number"
              className="w-full p-2 border border-gray-300 rounded-md mb-4"
              value={stockQuantity}
              onChange={handleStockChange}
              placeholder="Enter quantity"
            />
            <div className="flex justify-end space-x-2">
              <button
                className="bg-gray-400 text-white py-2 px-4 rounded"
                onClick={() => setIsStockModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
                onClick={handleStockSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discount Modal */}
      {isDiscountModalOpen && (
        <DiscountModal
          isOpen={isDiscountModalOpen}
          onClose={closeDiscountModal}
          productId={id}
        />
      )}

      {/* NEW: Featured Modal */}
      {isFeaturedModalOpen && (
        <FeaturedProductModal
          isOpen={isFeaturedModalOpen}
          onClose={closeFeaturedModal}
          productId={id}
        />
      )}

      {isEditModalOpen && (
        <ProductModal
          categories={categories}
          brands={brands}
          productId={productData?.id}
          showModal={isEditModalOpen}
          setShowModal={setIsEditModalOpen}
          onSuccess={handleProductModalSuccess}
        />
      )}

      {/* Variant Stock Details Modal */}
      {isVariantStockModalOpen && selectedColorForEdit && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96 max-w-full mx-4">
            <h2 className="text-lg font-semibold mb-4">
              Edit Stock Details - {selectedColorForEdit.color || "Color"}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Products
                </label>
                <input
                  type="number"
                  min="0"
                  className="w-full p-2 border border-gray-300 rounded-md"
                  value={variantStockFormData.totalProducts}
                  onChange={(e) =>
                    handleVariantStockFormChange(
                      "totalProducts",
                      e.target.value,
                    )
                  }
                  placeholder="Enter total products"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remaining Products
                </label>
                <input
                  type="number"
                  min="0"
                  className="w-full p-2 border border-gray-300 rounded-md"
                  value={variantStockFormData.remainingProduct}
                  onChange={(e) =>
                    handleVariantStockFormChange(
                      "remainingProduct",
                      e.target.value,
                    )
                  }
                  placeholder="Enter remaining products"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  MOQ (Minimum Order Quantity)
                </label>
                <input
                  type="number"
                  min="0"
                  max="10000"
                  className="w-full p-2 border border-gray-300 rounded-md"
                  value={variantStockFormData.moq}
                  onChange={(e) =>
                    handleVariantStockFormChange("moq", e.target.value)
                  }
                  placeholder="Enter MOQ (0-10000)"
                />
              </div>
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={variantStockFormData.isInStock}
                    onChange={(e) =>
                      handleVariantStockFormChange(
                        "isInStock",
                        e.target.checked,
                      )
                    }
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    In Stock
                  </span>
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                className="bg-gray-400 text-white py-2 px-4 rounded hover:bg-gray-500"
                onClick={() => {
                  setIsVariantStockModalOpen(false);
                  setSelectedColorForEdit(null);
                }}
                disabled={isVariantStockLoading === selectedColorForEdit?.id}
              >
                Cancel
              </button>
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleVariantStockSubmit}
                disabled={isVariantStockLoading === selectedColorForEdit?.id}
              >
                {isVariantStockLoading === selectedColorForEdit?.id
                  ? "Updating..."
                  : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductDetails;
